import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import IUserUI from "../../models/IUserUI";
import ISupplierModel from "../../models/ISupplierModel";
import IEmailDocument from "../../models/IEmailDocument";
import IEmailTemplateSummaryDTO from "../../models/DTO/IEmailTemplateSummaryDTO";
import IOnEmailTemplateCatalogDTO from "../../models/DTO/IOnEmailTemplateCatalogDTO";
import IOnGetEmailTemplateDTO from "../../models/DTO/IOnGetEmailTemplateDTO";
import IOnPreviewEmailTemplateDTO from "../../models/DTO/IOnPreviewEmailTemplateDTO";
import IOnSendTestEmailTemplateDTO from "../../models/DTO/IOnSendTestEmailTemplateDTO";
import { IEmailTemplateSampleOrderDTO } from "../../models/DTO/IOnGetEmailTemplateSampleOrdersDTO";
import {
    buildPreviewDto,
    deleteEmailTemplate,
    getEmailTemplateByKey,
    getEmailTemplateCatalog,
    getEmailTemplateSampleOrders,
    getEmailTemplates,
    IEmailTemplateKey,
    isQuoteTemplateKey,
    previewEmailTemplate,
    sendTestEmailTemplate,
    upsertEmailTemplate,
} from "../../services/EmailTemplateService";
import { documentsEqual } from "../../services/EmailDocumentUtils";

export type EmailPreviewMode = "desktop" | "mobile";

export interface UseEmailTemplateEditorOptions {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    /** Slot to open on load (from ?order_state_id= / ?email_event_type=). */
    initialKey?: IEmailTemplateKey | null;
    /** Preview debounce in ms (tests pass 0). */
    previewDelayMs?: number;
}

export interface UseEmailTemplateEditorResult {
    templates: IEmailTemplateSummaryDTO[];
    catalog: IOnEmailTemplateCatalogDTO | null;
    loading: boolean;
    error: string | null;
    clearError: () => void;

    selected: IOnGetEmailTemplateDTO | null;
    selectedKey: IEmailTemplateKey | null;
    scope: "order" | "account";
    /** True for the Quote Sent / Quote Converted slots: sample data and labels are quotes. */
    isQuoteSlot: boolean;
    document: IEmailDocument | null;
    setDocument: (next: IEmailDocument | ((prev: IEmailDocument) => IEmailDocument)) => void;
    name: string;
    setName: (name: string) => void;
    isActive: boolean;
    setIsActive: (active: boolean) => void;
    /** Something differs from what is saved (or nothing is saved yet for this slot). */
    dirty: boolean;
    saving: boolean;
    /** A template request is in flight: the selected slot and document may be about to change. */
    templateLoading: boolean;
    selectedBlockId: string | null;
    setSelectedBlockId: (id: string | null) => void;

    preview: IOnPreviewEmailTemplateDTO | null;
    previewLoading: boolean;
    previewError: string | null;
    previewMode: EmailPreviewMode;
    setPreviewMode: (mode: EmailPreviewMode) => void;
    sampleOrders: IEmailTemplateSampleOrderDTO[];
    sampleOrderGuid: string | undefined;
    setSampleOrderGuid: (guid: string | undefined) => void;
    searchSampleOrders: (q: string) => Promise<void>;

    selectTemplate: (key: IEmailTemplateKey) => Promise<void>;
    save: () => Promise<boolean>;
    /** Discard local edits, back to the saved (or starter) document. */
    revert: () => void;
    /** Delete the saved row so the built-in email is sent again; reloads the starter. */
    resetToDefault: () => Promise<boolean>;
    sendTest: () => Promise<IOnSendTestEmailTemplateDTO | null>;
}

const sameKey = (a: IEmailTemplateKey | null, b: IEmailTemplateKey | null): boolean => {
    return !!a && !!b && a.order_state_id === b.order_state_id && a.email_event_type === b.email_event_type;
};

/**
 * State and API calls for the email template editor page. Holds the working document, tracks
 * dirtiness against the last saved copy, and keeps a debounced server-rendered preview in sync.
 *
 * Takes the models as arguments so it can be tested without contexts.
 */
export const useEmailTemplateEditor = (options: UseEmailTemplateEditorOptions): UseEmailTemplateEditorResult => {
    const { userModel, supplierModel, initialKey, previewDelayMs = 500 } = options;

    const authRef = useRef({ userModel, supplierModel });
    authRef.current = { userModel, supplierModel };

    const [templates, setTemplates] = useState<IEmailTemplateSummaryDTO[]>([]);
    const [catalog, setCatalog] = useState<IOnEmailTemplateCatalogDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [selected, setSelected] = useState<IOnGetEmailTemplateDTO | null>(null);
    const [selectedKey, setSelectedKey] = useState<IEmailTemplateKey | null>(null);
    const [document, setDocumentState] = useState<IEmailDocument | null>(null);
    const [savedDocument, setSavedDocument] = useState<IEmailDocument | null>(null);
    const [name, setName] = useState("");
    const [savedName, setSavedName] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [savedIsActive, setSavedIsActive] = useState(true);
    const [saving, setSaving] = useState(false);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

    const [preview, setPreview] = useState<IOnPreviewEmailTemplateDTO | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [previewMode, setPreviewMode] = useState<EmailPreviewMode>("desktop");
    const [sampleOrders, setSampleOrders] = useState<IEmailTemplateSampleOrderDTO[]>([]);
    const [sampleOrderGuid, setSampleOrderGuid] = useState<string | undefined>(undefined);

    // Guards against an older in-flight response overwriting a newer one.
    const previewRequestIdRef = useRef(0);
    const selectRequestIdRef = useRef(0);
    const sampleOrdersRequestIdRef = useRef(0);

    const scope: "order" | "account" = selected?.scope ?? "order";
    const isQuoteSlot = isQuoteTemplateKey(selectedKey);

    const dirty = useMemo(() => {
        if (!document) {
            return false;
        }

        if (!selected?.isSaved) {
            return true;
        }

        return !documentsEqual(document, savedDocument) || name !== savedName || isActive !== savedIsActive;
    }, [document, savedDocument, selected, name, savedName, isActive, savedIsActive]);

    const setDocument = useCallback((next: IEmailDocument | ((prev: IEmailDocument) => IEmailDocument)) => {
        setDocumentState((prev) => {
            if (typeof next === "function") {
                return prev ? next(prev) : prev;
            }

            return next;
        });
    }, []);

    const applyLoaded = useCallback((payload: IOnGetEmailTemplateDTO) => {
        setSelected(payload);
        setDocumentState(payload.document);
        setSavedDocument(payload.isSaved ? payload.document : null);
        setName(payload.template.name || "");
        setSavedName(payload.template.name || "");
        setIsActive(payload.template.is_active !== false);
        setSavedIsActive(payload.template.is_active !== false);
        setSelectedBlockId(null);
        setPreview(null);
        setPreviewError(null);
    }, []);

    const selectTemplate = useCallback(
        async (key: IEmailTemplateKey) => {
            const requestId = ++selectRequestIdRef.current;
            setTemplateLoading(true);
            setError(null);

            try {
                const payload = await getEmailTemplateByKey(authRef.current, key);

                // Key and payload move together so save/test/preview never pair a new key with the old document.
                if (requestId === selectRequestIdRef.current) {
                    setSelectedKey(key);
                    applyLoaded(payload);
                }
            } catch (err: any) {
                if (requestId === selectRequestIdRef.current) {
                    setError(err?.message || "Failed to load email template");
                }
            } finally {
                if (requestId === selectRequestIdRef.current) {
                    setTemplateLoading(false);
                }
            }
        },
        [applyLoaded]
    );

    const refreshList = useCallback(async () => {
        const list = await getEmailTemplates(authRef.current);
        setTemplates(list);
        return list;
    }, []);

    // Initial load: catalog + list (+ the initial slot, or the first slot).
    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);

            try {
                const [cat, list] = await Promise.all([getEmailTemplateCatalog(authRef.current), getEmailTemplates(authRef.current)]);

                if (cancelled) {
                    return;
                }

                setCatalog(cat);
                setTemplates(list);

                const first = list[0];
                const key: IEmailTemplateKey | null =
                    initialKey && (initialKey.order_state_id || initialKey.email_event_type)
                        ? initialKey
                        : first
                          ? { order_state_id: first.order_state_id, email_event_type: first.email_event_type }
                          : null;

                if (key) {
                    await selectTemplate(key);
                }
            } catch (err: any) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load email templates");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // initialKey is read once on mount by design.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectTemplate]);

    // Debounced server preview.
    useEffect(() => {
        if (!document || !selectedKey) {
            return;
        }

        const requestId = ++previewRequestIdRef.current;
        setPreviewLoading(true);

        const timer = setTimeout(async () => {
            try {
                const result = await previewEmailTemplate(authRef.current, buildPreviewDto(selectedKey, document, sampleOrderGuid));

                if (requestId === previewRequestIdRef.current) {
                    setPreview(result);
                    setPreviewError(null);
                }
            } catch (err: any) {
                if (requestId === previewRequestIdRef.current) {
                    setPreviewError(err?.errors?.[0] || err?.message || "Preview failed");
                }
            } finally {
                if (requestId === previewRequestIdRef.current) {
                    setPreviewLoading(false);
                }
            }
        }, previewDelayMs);

        return () => clearTimeout(timer);
    }, [document, selectedKey, sampleOrderGuid, previewDelayMs]);

    const searchSampleOrders = useCallback(
        async (q: string) => {
            const requestId = ++sampleOrdersRequestIdRef.current;

            try {
                const payload = await getEmailTemplateSampleOrders(authRef.current, q, isQuoteSlot);

                if (requestId === sampleOrdersRequestIdRef.current) {
                    setSampleOrders(payload.orders);
                }
            } catch (err: any) {
                if (requestId === sampleOrdersRequestIdRef.current) {
                    setError(err?.message || "Failed to load orders");
                }
            }
        },
        [isQuoteSlot]
    );

    // Sample orders only matter for order-scoped slots. Reload when switching between quote
    // and order slots (different result sets), and drop a pick that no longer applies.
    const sampleKindRef = useRef<string | null>(null);

    useEffect(() => {
        if (scope !== "order" || !selected) {
            return;
        }

        const kind = isQuoteSlot ? "quotes" : "orders";

        if (sampleKindRef.current !== kind) {
            sampleKindRef.current = kind;
            setSampleOrderGuid(undefined);
            searchSampleOrders("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, selected, isQuoteSlot]);

    const save = useCallback(async (): Promise<boolean> => {
        if (!document || !selectedKey) {
            return false;
        }

        setSaving(true);
        setError(null);

        try {
            const template = await upsertEmailTemplate(authRef.current, {
                guid: selected?.template.guid || undefined,
                ...selectedKey,
                name: name || null,
                document,
                is_active: isActive,
            });

            setSelected((prev) => (prev ? { ...prev, template, isSaved: true, warnings: [] } : prev));
            setSavedDocument(document);
            setSavedName(name);
            setSavedIsActive(isActive);
            setTemplates((prev) => prev.map((t) => (t.key === template.key ? template : t)));

            return true;
        } catch (err: any) {
            setError(err?.errors?.length ? err.errors.join(" ") : err?.message || "Failed to save email template");
            return false;
        } finally {
            setSaving(false);
        }
    }, [document, selectedKey, selected, name, isActive]);

    const revert = useCallback(() => {
        if (!selected) {
            return;
        }

        setDocumentState(savedDocument || selected.document);
        setName(savedName);
        setIsActive(savedIsActive);
        setSelectedBlockId(null);
    }, [selected, savedDocument, savedName, savedIsActive]);

    const resetToDefault = useCallback(async (): Promise<boolean> => {
        if (!selectedKey) {
            return false;
        }

        setSaving(true);
        setError(null);

        try {
            if (selected?.template.guid) {
                await deleteEmailTemplate(authRef.current, selected.template.guid, selectedKey);
            }

            await refreshList();
            await selectTemplate(selectedKey);

            return true;
        } catch (err: any) {
            setError(err?.message || "Failed to reset email template");
            return false;
        } finally {
            setSaving(false);
        }
    }, [selectedKey, selected, refreshList, selectTemplate]);

    const sendTest = useCallback(async (): Promise<IOnSendTestEmailTemplateDTO | null> => {
        if (!document || !selectedKey) {
            return null;
        }

        setError(null);

        try {
            return await sendTestEmailTemplate(authRef.current, buildPreviewDto(selectedKey, document, sampleOrderGuid));
        } catch (err: any) {
            setError(err?.errors?.length ? err.errors.join(" ") : err?.message || "Failed to send test email");
            return null;
        }
    }, [document, selectedKey, sampleOrderGuid]);

    return {
        templates,
        catalog,
        loading,
        error,
        clearError: () => setError(null),
        selected,
        selectedKey,
        scope,
        isQuoteSlot,
        document,
        setDocument,
        name,
        setName,
        isActive,
        setIsActive,
        dirty,
        saving,
        templateLoading,
        selectedBlockId,
        setSelectedBlockId,
        preview,
        previewLoading,
        previewError,
        previewMode,
        setPreviewMode,
        sampleOrders,
        sampleOrderGuid,
        setSampleOrderGuid,
        searchSampleOrders,
        selectTemplate: useCallback(
            async (key: IEmailTemplateKey) => {
                if (sameKey(key, selectedKey)) {
                    return;
                }

                await selectTemplate(key);
            },
            [selectTemplate, selectedKey]
        ),
        save,
        revert,
        resetToDefault,
        sendTest,
    };
};

export default useEmailTemplateEditor;
